// BUG_REPORTER — TEMPORARY FEATURE. Delete this folder per docs/BUG_REPORTER.md.
//
// The Conversation tab: everything said and done about one report on one line of
// time, oldest first, with the comment box at the bottom (owner's decision
// 2026-09-18, docs/plans/DECISIONS.md D47). It had been two sections under the
// description and the screenshot — "Comments" from ClickUp and a USC "Discussion"
// — and the owner found it cluttered: reading a reply meant scrolling past, and
// waiting for, the screenshot.
//
// What each entry is, and where it lives, has not changed:
//   - the development team's notes are the ticket's [client-facing] comments,
//     read live from ClickUp and read-only here (D37, D38);
//   - a comment written here is stored only in USC and never reaches ClickUp
//     (BR-1, O-27 PROVISIONAL); only your own can be edited or deleted;
//   - status changes, and links to and from other reports (D47).
//
// Plain text. Every body is a React text node, so markup somebody types shows as
// the characters they typed — never MarkdownContent, never injected as markup
// (the ratchet in components/UntrustedHtmlFrame.test.tsx holds that line).

import React, { useRef, useState } from 'react';
import { History, Link2 } from 'lucide-react';
import {
  BUG_STATUS_LABEL,
  COMMENT_MAX_CHARS,
  addBugReportComment,
  deleteBugReportComment,
  editBugReportComment,
  newSubmissionId,
  type BugDiscussionComment,
  type BugLinkEvent,
  type BugReportComment,
  type BugReportInstance,
  type BugReportReply,
  type BugReportSummary,
  type BugStatusChange,
} from '../../lib/bugReportApi';
import { formatDateTime } from '../../utils/dateFormat';
import Button from '../../components/ui/Button';
import { useConfirm } from '../../components/ui/ConfirmProvider';
import { ErrorState, LoadingIndicator } from '../../components/ui/States';
import { FOCUS } from '../../components/ui/tokens';
import type { TeamNotes } from './useTeamNotes';

type Entry =
  | { kind: 'status'; key: string; at: string; change: BugStatusChange }
  | { kind: 'comment'; key: string; at: string; comment: BugDiscussionComment }
  | { kind: 'note'; key: string; at: string; note: BugReportComment }
  | { kind: 'link'; key: string; at: string; event: BugLinkEvent }
  | { kind: 'instance'; key: string; at: string; instance: BugReportInstance };

/** Everything on one line of time, oldest first. A note ClickUp sent without a date sits with the report's filing. */
function timelineOf(report: BugReportSummary, notes: BugReportComment[]): Entry[] {
  const entries: Entry[] = [
    ...(report.statusHistory ?? []).map((change, i): Entry => ({ kind: 'status', key: `status-${i}`, at: change.at, change })),
    ...(report.discussion ?? []).map((comment): Entry => ({ kind: 'comment', key: comment.id, at: comment.createdAt, comment })),
    ...notes.map((note): Entry => ({ kind: 'note', key: `note-${note.id}`, at: note.createdAt ?? report.createdAt, note })),
    ...(report.linkHistory ?? []).map((event, i): Entry => ({ kind: 'link', key: `link-${i}`, at: event.at, event })),
    ...(report.instances ?? []).map(
      (instance): Entry => ({ kind: 'instance', key: `instance-${instance.id}`, at: instance.linkedAt ?? instance.createdAt, instance })
    ),
  ];
  return entries.sort((a, b) => Date.parse(a.at) - Date.parse(b.at));
}

function linkEventText(event: BugLinkEvent): string {
  const by = event.byName ? ` by ${event.byName}` : '';
  if (event.action === 'unlinked') return `Unlinked from ${event.ticketNumber}${by}`;
  if (event.movedWith) return `Moved to ${event.ticketNumber} together with ${event.movedWith}${by}`;
  if (event.kind === 'came_back') return `Linked to ${event.ticketNumber} — it came back after that was fixed —${by}`;
  return `Linked to ${event.ticketNumber} as the same problem${by}`;
}

/** Two letters for the avatar: "Kaushal Bakrania" -> "KB". */
function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  const letters = parts.length > 1 ? parts[0][0] + parts[parts.length - 1][0] : parts[0].slice(0, 2);
  return letters.toUpperCase();
}

/** A line in the timeline that records something done rather than said. */
const EventLine: React.FC<{ icon: React.ReactNode; at: string; children: React.ReactNode }> = ({ icon, at, children }) => (
  <li className="flex flex-wrap items-center gap-x-2 gap-y-0.5 px-1 text-xs text-slate-500">
    {icon}
    <span>{children}</span>
    <time dateTime={at}>{formatDateTime(at)}</time>
  </li>
);

const TeamNoteBody: React.FC<{ note: BugReportReply; reply?: boolean }> = ({ note, reply = false }) => (
  <div className="flex gap-3">
    <span
      aria-hidden="true"
      className={`flex shrink-0 items-center justify-center rounded-full bg-primary-50 text-xs font-semibold text-primary-600 ${
        reply ? 'h-7 w-7' : 'h-8 w-8'
      }`}
    >
      {initialsOf(note.author)}
    </span>
    <div className="min-w-0 flex-1">
      <p className="flex flex-wrap items-baseline gap-x-2 text-xs">
        <span className="font-semibold text-slate-900">{note.author}</span>
        <span className="rounded bg-primary-50 px-1.5 py-0.5 text-xs font-medium text-primary-600">Development team</span>
        {note.createdAt ? (
          <time dateTime={note.createdAt} className="text-slate-500">
            {formatDateTime(note.createdAt)}
          </time>
        ) : null}
      </p>
      {note.text ? (
        <p className="mt-1 whitespace-pre-wrap break-words text-sm leading-[1.55] text-slate-700">{note.text}</p>
      ) : (
        <p className="mt-1 text-sm italic text-slate-500">No text — this note may only hold an attachment.</p>
      )}
    </div>
  </div>
);

const BOX = `block w-full resize-y rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 ${FOCUS}`;

export const BugReportConversation: React.FC<{
  report: BugReportSummary;
  notes: TeamNotes;
  /** Every write answers with the whole report; the page puts it on screen. */
  onChanged: (report: BugReportSummary) => void;
  /** Opens another report — an instance named in the timeline. */
  onOpen: (id: string) => void;
}> = ({ report, notes, onChanged, onOpen }) => {
  const confirm = useConfirm();
  const [draft, setDraft] = useState('');
  const [editing, setEditing] = useState<{ id: string; text: string } | null>(null);
  // 'new' while sending, or the id of the comment being saved or deleted.
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // One id per draft. A retry after a failure re-sends the same id, so the server
  // still keeps one comment; a fresh id is minted only once the draft is stored.
  const clientId = useRef(newSubmissionId());
  // A ref, not state: two clicks inside one frame both run before React re-renders
  // the button as disabled.
  const inFlight = useRef(false);

  const run = async (key: string, action: () => Promise<{ data: { report: BugReportSummary } }>) => {
    if (inFlight.current) return false;
    inFlight.current = true;
    setBusy(key);
    setError(null);
    try {
      const res = await action();
      onChanged(res.data.report);
      return true;
    } catch (err: any) {
      setError(err?.message || 'That did not save. Try again.');
      return false;
    } finally {
      inFlight.current = false;
      setBusy(null);
    }
  };

  const send = async () => {
    const text = draft.trim();
    if (!text) return;
    if (await run('new', () => addBugReportComment(report.id, clientId.current, text))) {
      setDraft('');
      clientId.current = newSubmissionId();
    }
  };

  const saveEdit = async () => {
    if (!editing) return;
    const text = editing.text.trim();
    if (!text) return;
    if (await run(editing.id, () => editBugReportComment(report.id, editing.id, text))) setEditing(null);
  };

  const remove = async (comment: BugDiscussionComment) => {
    if (inFlight.current) return;
    const confirmed = await confirm({
      title: 'Delete this comment?',
      summary: 'It is removed for everyone who can see this report.',
      tone: 'error',
      confirmLabel: 'Delete',
    });
    if (!confirmed) return;
    await run(comment.id, () => deleteBugReportComment(report.id, comment.id));
  };

  const entries = timelineOf(report, notes.result?.comments ?? []);
  const headingId = `bug-conversation-${report.id}`;
  const composerId = `bug-conversation-new-${report.id}`;

  return (
    <section aria-labelledby={headingId} className="space-y-3">
      <h3 id={headingId} className="sr-only">
        Conversation
      </h3>

      {notes.error ? (
        <ErrorState message={notes.error} onRetry={notes.retry} />
      ) : !notes.result ? (
        <LoadingIndicator label="Loading the development team's notes…" />
      ) : null}

      {entries.length === 0 && notes.result ? (
        <p className="rounded-lg border border-dashed border-slate-300 bg-white px-4 py-3 text-sm text-slate-500">
          No replies yet. When the development team replies, it shows here. Ask a question or add detail below.
        </p>
      ) : entries.length > 0 ? (
        <ol className="space-y-2">
          {entries.map((entry) => {
            if (entry.kind === 'status') {
              const { from, to, via } = entry.change;
              return (
                <EventLine key={entry.key} at={entry.at} icon={<History aria-hidden="true" size={14} className="shrink-0 text-slate-400" />}>
                  Status changed from <span className="font-medium text-slate-700">{BUG_STATUS_LABEL[from] ?? from}</span> to{' '}
                  <span className="font-medium text-slate-700">{BUG_STATUS_LABEL[to] ?? to}</span>
                  {via ? ` (following ${via})` : ''}
                </EventLine>
              );
            }
            if (entry.kind === 'link') {
              return (
                <EventLine key={entry.key} at={entry.at} icon={<Link2 aria-hidden="true" size={14} className="shrink-0 text-slate-400" />}>
                  {linkEventText(entry.event)}
                </EventLine>
              );
            }
            if (entry.kind === 'instance') {
              const { instance } = entry;
              return (
                <EventLine key={entry.key} at={entry.at} icon={<Link2 aria-hidden="true" size={14} className="shrink-0 text-slate-400" />}>
                  <button
                    type="button"
                    onClick={() => onOpen(instance.id)}
                    className={`font-medium text-primary-600 hover:underline ${FOCUS}`}
                  >
                    {instance.ticketNumber}
                  </button>{' '}
                  ({instance.title}) was {instance.kind === 'came_back' ? 'linked here: it came back after this was fixed' : 'linked here as the same problem'}
                  {instance.linkedByName ? ` by ${instance.linkedByName}` : ''}
                </EventLine>
              );
            }
            if (entry.kind === 'note') {
              const { note } = entry;
              return (
                <li key={entry.key} className="rounded-lg border border-slate-200 border-l-2 border-l-primary-500 bg-white p-3">
                  <TeamNoteBody note={note} />
                  {note.replies.length > 0 ? (
                    <ol aria-label="Replies" className="ml-4 mt-3 space-y-3 border-l-2 border-slate-100 pl-4">
                      {note.replies.map((reply) => (
                        <li key={reply.id}>
                          <TeamNoteBody note={reply} reply />
                        </li>
                      ))}
                    </ol>
                  ) : null}
                </li>
              );
            }
            const { comment } = entry;
            return (
              <li key={entry.key} className="rounded-lg border border-slate-200 bg-white p-3">
                <p className="flex flex-wrap items-baseline gap-x-2 text-xs">
                  <span className="font-semibold text-slate-900">{comment.authorName}</span>
                  <time dateTime={entry.at} className="text-slate-500">
                    {formatDateTime(entry.at)}
                  </time>
                  {comment.editedAt ? (
                    <span className="text-slate-500" title={`Edited ${formatDateTime(comment.editedAt)}`}>
                      (edited)
                    </span>
                  ) : null}
                </p>

                {editing?.id === comment.id ? (
                  <div className="mt-2 space-y-2">
                    <label htmlFor={`edit-${comment.id}`} className="sr-only">
                      Edit your comment
                    </label>
                    <textarea
                      id={`edit-${comment.id}`}
                      value={editing.text}
                      maxLength={COMMENT_MAX_CHARS}
                      rows={3}
                      onChange={(e) => setEditing({ id: comment.id, text: e.target.value })}
                      className={BOX}
                    />
                    <div className="flex justify-end gap-2">
                      <Button variant="ghost" size="sm" onClick={() => setEditing(null)} disabled={busy !== null}>
                        Cancel
                      </Button>
                      <Button variant="primary" size="sm" onClick={saveEdit} loading={busy === comment.id} disabled={!editing.text.trim()}>
                        Save
                      </Button>
                    </div>
                  </div>
                ) : (
                  <p className="mt-1 whitespace-pre-wrap break-words text-sm leading-[1.55] text-slate-700">{comment.body}</p>
                )}

                {comment.isMine && editing?.id !== comment.id ? (
                  <div className="mt-2 flex gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setEditing({ id: comment.id, text: comment.body })}
                      disabled={busy !== null}
                    >
                      Edit
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => remove(comment)} loading={busy === comment.id} disabled={busy !== null}>
                      Delete
                    </Button>
                  </div>
                ) : null}
              </li>
            );
          })}
        </ol>
      ) : null}

      {notes.result?.truncated ? (
        <p className="text-xs text-slate-500">
          The development team has written more on this report than can be shown here; the latest notes are shown.
        </p>
      ) : null}

      {error ? (
        <p role="alert" className="text-sm text-red-700">
          {error}
        </p>
      ) : null}

      <div className="space-y-2 border-t border-slate-200 pt-3">
        <label htmlFor={composerId} className="block text-xs font-medium text-slate-700">
          Add a comment
        </label>
        <textarea
          id={composerId}
          value={draft}
          maxLength={COMMENT_MAX_CHARS}
          rows={3}
          placeholder="Ask a question or add detail. Plain text."
          onChange={(e) => setDraft(e.target.value)}
          className={BOX}
        />
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-xs text-slate-500">
            Kept in USC. Everyone who can see this report can read it.
            {draft.length > COMMENT_MAX_CHARS - 200 ? (
              <span className="ml-1 tabular-nums">
                {draft.length} / {COMMENT_MAX_CHARS}
              </span>
            ) : null}
          </p>
          <Button variant="primary" size="sm" onClick={send} loading={busy === 'new'} disabled={!draft.trim() || busy !== null}>
            Send
          </Button>
        </div>
      </div>
    </section>
  );
};
